 


// Finance/CashTransfer/CashTransferIndex.tsx
import { useEffect, useState } from 'react';
import Breadcrumb from '@components/Shared/Breadcrumb';
import { Card, CardFooter, CardHeader } from '@components/card/card';
import { useTranslation } from 'react-i18next';
import Loading from '@components/Shared/Loader/Loading';
import NeatixModal from '@components/Modal/NeatixModal';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { getPagedData, setEndpoint, setFilters } from '@store/DynamicSlice/dynamicTableSlice';
import { DynamicFilter } from '@Helper/DynamicFilter/DynamicFilter';
import { DynamicPagination } from '@Helper/DynamicFilter/DynamicPagination';
import { Can } from '@Permisstions/Can';
import Select from 'react-select';
import confirmAction from '@components/Shared/Alert/confirmAction';
import CustomAlert from '@components/Shared/Alert/CustomAlert';
import useGetBankAccountsList from '@hooks/Finance/BankAccounts/useGetBankAccountsList';
import { LuUpload, LuDownload } from 'react-icons/lu';
import CashTransferForm from './CashTransferForm';

const TABLE_KEY = 'CashTransfer_Index';

const Index = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedTransferId, setSelectedTransferId] = useState<string | null>(null);
  const [selectedTransfer, setSelectedTransfer] = useState<any>(null);

  // ✅ Filters
  const [fromAccountFilter, setFromAccountFilter] = useState<any>(null);
  const [toAccountFilter, setToAccountFilter] = useState<any>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const [minAmountFilter, setMinAmountFilter] = useState('');
  const [maxAmountFilter, setMaxAmountFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<any>(null);

  const [activeFiltersCount, setActiveFiltersCount] = useState(0);

  const { t, i18n } = useTranslation();
  const { OptionsBankAccounts, loading: accountsLoading } = useGetBankAccountsList();

  const dispatch = useAppDispatch();
  const tableState = useAppSelector(state => state.dynamicTable[TABLE_KEY]);
  const data = tableState?.data ?? [];
  const loading = tableState?.loading ?? false;
  const error = tableState?.error ?? null;
  const filters = tableState?.filters ?? [];

  useEffect(() => {
    dispatch(setEndpoint({ tableKey: TABLE_KEY, endpoint: 'Finance/GetFinCashTransfers_Paged' }));
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  }, [dispatch]);

  useEffect(() => {
    setActiveFiltersCount(filters.length);
  }, [filters]);

  const generateFilterId = () => `filter-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

  const applyFilters = () => {
    const newFilters: any[] = [];

    if (fromAccountFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'fromBankAccountId',
        operator: 0,
        value: fromAccountFilter.value
      });
    }

    if (toAccountFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'toBankAccountId',
        operator: 0,
        value: toAccountFilter.value
      });
    }

    if (dateFrom) {
      newFilters.push({
        id: generateFilterId(),
        field: 'transferDate',
        operator: 3,
        value: dateFrom
      });
    }

    if (dateTo) {
      newFilters.push({
        id: generateFilterId(),
        field: 'transferDate',
        operator: 5,
        value: dateTo
      });
    }

    if (minAmountFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'amount',
        operator: 3,
        value: minAmountFilter
      });
    }

    if (maxAmountFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'amount',
        operator: 5,
        value: maxAmountFilter
      });
    }

    // search on fromBankAccountName (as you did)
    if (searchFilter) {
      newFilters.push({
        id: generateFilterId(),
        field: 'fromBankAccountName',
        operator: 7,
        value: searchFilter
      });
    }

    // status filter (optional)
    if (statusFilter) {
      if (statusFilter.value === 'true' || statusFilter.value === 'false') {
        newFilters.push({
          id: generateFilterId(),
          field: 'confirmFlag',
          operator: 0,
          value: statusFilter.value === 'true'
        });
      } else if (statusFilter.value === 'posted') {
        newFilters.push({
          id: generateFilterId(),
          field: 'postedFlag',
          operator: 0,
          value: true
        });
      } else if (statusFilter.value === 'notPosted') {
        newFilters.push({
          id: generateFilterId(),
          field: 'postedFlag',
          operator: 0,
          value: false
        });
      }
    }

    dispatch(setFilters({ tableKey: TABLE_KEY, filters: newFilters }));
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  };

  const clearFilters = (): void => {
    setFromAccountFilter(null);
    setToAccountFilter(null);
    setDateFrom('');
    setDateTo('');
    setSearchFilter('');
    setMinAmountFilter('');
    setMaxAmountFilter('');
    setStatusFilter(null);

    dispatch(setFilters({ tableKey: TABLE_KEY, filters: [] }));
    dispatch(getPagedData({ tableKey: TABLE_KEY }));
  };

  const handleEdit = (transfer: any) => {
    setSelectedTransfer(transfer);
    setSelectedTransferId(transfer.id);
    setShowEditModal(true);
  };

  const handleDelete = async (id: string) => {
    const isConfirmed = await confirmAction('هل أنت متأكد من حذف التحويل النقدي؟');
    if (!isConfirmed) return;

    try {
      // TODO: call delete endpoint/slice
      CustomAlert({ action: 'Delete', msg: t('تم حذف التحويل النقدي بنجاح') });
      dispatch(getPagedData({ tableKey: TABLE_KEY }));
    } catch (e: any) {
      CustomAlert({ action: 'Error', msg: e?.message || t('فشل حذف التحويل النقدي') });
    }
  };

  const displayData = data;

  // ======= UI Helpers (like ApPayments style) =======
  const formatAmount = (amount: number) =>
    new Intl.NumberFormat(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(amount || 0);

  const formatDate = (d: any) => {
    if (!d) return '';
    return String(d).split('T')[0];
  };

  // دعم مرن للإلغاء إذا كان موجود في البيانات
  const isCancelled = (item: any) => {
    const s = (item?.status || '').toString().toLowerCase();
    return (
      s === 'cancelled' ||
      item?.cancelledFlag === true ||
      item?.voidFlag === true ||
      item?.isCancelled === true ||
      item?.cancelFlag === true
    );
  };

  const getRowClasses = (item: any) => {
    if (isCancelled(item)) return 'bg-light bg-opacity-25 border-end border-1 border-danger';
    if (item?.confirmFlag === false) return 'bg-light bg-opacity-10';
    return '';
  };

  const calculateTotal = (amount: number, fees: number, variance: number) => (amount || 0) + (fees || 0) + (variance || 0);

  const formatAccountNumber = (accountNumber: string) => (accountNumber ? accountNumber : '');

  const getConfirmBadge = (confirmFlag: boolean, cancelled: boolean) => {
    const baseClass = cancelled ? 'opacity-60' : '';
    return confirmFlag ? (
      <span className={`badge bg-success text-white px-2 py-1 fw-bold ${baseClass}`} title='تم التأكيد'>
        <i className='fas fa-check-circle me-1'></i>
        تم التأكيد
      </span>
    ) : (
      <span className={`badge bg-warning text-dark px-2 py-1 fw-bold ${baseClass}`} title='مسودة'>
        <i className='fas fa-file-edit me-1'></i>
        مسودة
      </span>
    );
  };

  const getPostedBadge = (postedFlag: boolean, cancelled: boolean) => {
    const baseClass = cancelled ? 'opacity-60' : '';
    return postedFlag ? (
      <span className={`badge bg-primary text-white px-2 py-1 fw-bold ${baseClass}`} title='مرحل'>
        <i className='fas fa-check-double me-1'></i>
        مرحل
      </span>
    ) : (
      <span className={`badge bg-secondary text-white px-2 py-1 fw-bold ${baseClass}`} title='غير مرحل'>
        <i className='fas fa-clock me-1'></i>
        غير مرحل
      </span>
    );
  };

  // ✅ رقم التحويل + التاريخ + code (مثل VoucherWithDate)
  const renderTransferWithDate = (code: string, date: string, cancelled: boolean) => {
    const d = formatDate(date);

    return (
      <div className='d-flex flex-column'>
        <div className='fw-bold'>
          {cancelled ? (
            <div className='d-flex align-items-center'>
              <span className='text-danger text-decoration-line-through me-2' style={{ letterSpacing: '0.05em' }}>
                {code || '—'}
              </span>
              <span className='badge bg-danger badge-sm'>
                <i className='fas fa-ban'></i>
              </span>
            </div>
          ) : (
            <span className='text-theme' style={{ letterSpacing: '0.05em' }}>
              {code || '—'}
            </span>
          )}
        </div>

        <small className={`text-muted ${cancelled ? 'text-decoration-line-through' : ''}`}>
          <i className='fas fa-calendar-alt me-1'></i>
          {d || '—'}
        </small>
      </div>
    );
  };

  // ✅ من/إلى في عمود واحد
  const renderFromToAccounts = (item: any, cancelled: boolean) => {
    const fromName = i18n.language === 'en' ? item.fromBankAccountName : item.fromBankAccountName2;
    const toName = i18n.language === 'en' ? item.toBankAccountName : item.toBankAccountName2;

    const fromNo = formatAccountNumber(item.fromBankAccountNumber);
    const toNo = formatAccountNumber(item.toBankAccountNumber);

    const textClass = cancelled ? 'text-decoration-line-through opacity-70' : '';

    return (
      <div className='d-flex flex-column gap-2'>
        {/* FROM */}
        <div className='d-flex align-items-start gap-2'>
          <LuUpload className='text-danger mt-1' />
          <div className={`d-flex flex-column ${textClass}`}>
            <strong>{fromName || '—'}</strong>
            {fromNo && (
              <small className='text-muted'>
                <i className='fas fa-hashtag me-1'></i>
                {fromNo}
              </small>
            )}
          </div>
        </div>

        {/* TO */}
        <div className='d-flex align-items-start gap-2'>
          <LuDownload className='text-success mt-1' />
          <div className={`d-flex flex-column ${textClass}`}>
            <strong>{toName || '—'}</strong>
            {toNo && (
              <small className='text-muted'>
                <i className='fas fa-hashtag me-1'></i>
                {toNo}
              </small>
            )}
          </div>
        </div>
      </div>
    );
  };

  // ✅ المبلغ + التفاصيل في عمود واحد (مثل Amount/Description)
  const renderAmountWithDetails = (amount: number, fees: number, variance: number, cancelled: boolean) => {
    const total = calculateTotal(amount, fees, variance);

    const amountClass = cancelled ? 'text-decoration-line-through opacity-70' : 'text-theme';
    const mutedClass = cancelled ? 'text-decoration-line-through opacity-60' : '';

    return (
      <div className='d-flex flex-column'>
        <span className={`fw-bold fs-6 ${amountClass}`}>
          {formatAmount(total)}
          {cancelled && (
            <span className='ms-2 badge bg-danger badge-sm'>
              <i className='fas fa-ban'></i>
            </span>
          )}
        </span>

        <small className={`text-muted mt-1 ${mutedClass}`}>
          <i className='fas fa-coins me-1'></i> {t('المبلغ')}: {formatAmount(amount)}
        </small>

        <small className={`text-muted ${mutedClass}`}>
          <i className='fas fa-receipt me-1'></i> {t('رسوم')}: {fees ? formatAmount(fees) : '—'}
        </small>

        <small className={`text-muted ${mutedClass}`}>
          <i className='fas fa-random me-1'></i> {t('فروقات')}: {variance ? formatAmount(variance) : '—'}
        </small>
      </div>
    );
  };

  // ✅ الحالة / الترحيل في عمود واحد
  const renderStatusWithPosting = (item: any, cancelled: boolean) => {
    return (
      <div className='d-flex flex-column align-items-center gap-1'>
        {getConfirmBadge(!!item.confirmFlag, cancelled)}
        {getPostedBadge(!!item.postedFlag, cancelled)}
      </div>
    );
  };

  // ✅ تعريف الحقول للفلتر الديناميكي (كما عندك)
  const fields: any = [
    { name: 'fromBankAccountId', label: 'من الحساب', type: 'options', options: OptionsBankAccounts },
    { name: 'toBankAccountId', label: 'إلى الحساب', type: 'options', options: OptionsBankAccounts },
    { name: 'transferDate', label: 'تاريخ التحويل', type: 'date' },
    { name: 'amount', label: 'المبلغ', type: 'number' },
    { name: 'tranferFees', label: 'رسوم التحويل', type: 'number' },
    { name: 'transferVariance', label: 'فروقات التحويل', type: 'number' },
    { name: 'confirmFlag', label: 'حالة التأكيد', type: 'boolean' },
    { name: 'postedFlag', label: 'حالة الترحيل', type: 'boolean' }
  ];

  const breadcrumbList = [
    { name: t('Home'), link: ':moduleHome' },
    { name: 'التحويلات النقديــة', link: null }
  ];

  return (
    <>
      <div className='d-flex flex-wrap align-items-center gap-2'>
        <Breadcrumb BreadcrumbList={breadcrumbList} PageName={'التحويلات النقديــة'} />

        <div className='ms-auto'>
          <button className='btn btn-theme' onClick={() => setShowAddModal(true)}>
            <i className='fa fa-plus-circle fa-fw me-1'></i>
            تحويل نقدي جديد
          </button>
        </div>
      </div>

      {/* Filters Card (keep same UI) */}
      <Card className='mb-3 shadow bg-body1'>
        <CardHeader>
          <div className='row g-2'>
            <div className='col-lg-3'>
              <div className='input-group'>
                <span className='input-group-text fw-bold'>
                  <LuUpload className='text-danger' />
                </span>
                <Select
                  value={fromAccountFilter}
                  onChange={setFromAccountFilter}
                  options={OptionsBankAccounts}
                  placeholder='من حساب'
                  isClearable
                  isSearchable
                  className='flex-grow-1'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  isLoading={accountsLoading}
                />
              </div>
            </div>

            <div className='col-lg-3'>
              <div className='input-group'>
                <span className='input-group-text fw-bold'>
                  <LuDownload className='text-success' />
                </span>
                <Select
                  value={toAccountFilter}
                  onChange={setToAccountFilter}
                  options={OptionsBankAccounts}
                  placeholder='إلى حساب'
                  isClearable
                  isSearchable
                  className='flex-grow-1'
                  classNamePrefix='react-select'
                  menuPortalTarget={document.body}
                  styles={{ menuPortal: (base: any) => ({ ...base, zIndex: 9999 }) }}
                  isLoading={accountsLoading}
                />
              </div>
            </div>

            <div className='col-lg-3'>
              <div className='input-group'>
                <span className='input-group-text'>
                  <i className='fas fa-calendar-alt text-muted'></i>
                </span>
                <input
                  type='date'
                  className='form-control border-start-0'
                  value={dateFrom}
                  onChange={e => setDateFrom(e.target.value)}
                  placeholder='من تاريخ'
                />
                <span className='input-group-text'>-</span>
                <input
                  type='date'
                  className='form-control'
                  value={dateTo}
                  onChange={e => setDateTo(e.target.value)}
                  placeholder='إلى تاريخ'
                />
              </div>
            </div>

            <div className='col-lg-3 text-end'>
              <div className='d-flex justify-content-end'>
                <button
                  className='btn btn-outline-secondary me-2'
                  type='button'
                  onClick={clearFilters}
                  title='مسح الفلاتر'
                  disabled={activeFiltersCount === 0}
                >
                  <i className='fas fa-times fa-fw'></i>
                </button>

                <button
                  className='btn btn-theme me-2'
                  type='button'
                  onClick={applyFilters}
                  title='تطبيق الفلاتر'
                  disabled={!fromAccountFilter && !toAccountFilter && !dateFrom && !dateTo && !searchFilter && !minAmountFilter && !maxAmountFilter && !statusFilter}
                >
                  <i className='fas fa-filter fa-fw'></i>
                </button>

                <button className='btn btn-outline-theme' onClick={() => setShowFilters(true)} title='فلاتر متقدمة'>
                  <i className='fas fa-sliders-h fa-fw'></i>
                </button>
              </div>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Main Table Card (NEW Display like ApPayments) */}
      <Card className='mb-4'>
        <Loading loading={loading} error={error}>
          <div className='table-responsive'>
            <table className='table table-hover mb-0'>
              <thead className='bg-theme bg-opacity-25 fw-bold'>
                <tr>
                  <th className='small'>رقم التحويل / التاريخ</th>
                  <th className='small'>من / إلى</th>
                  <th className='small'>المبلغ / التفاصيل</th>
                  <th className='small text-center'>الحالة / الترحيل</th>
                  <th className='small text-center'>الإجراءات</th>
                </tr>
              </thead>

              <tbody>
                {displayData.length > 0 ? (
                  displayData.map((item: any) => {
                    const cancelled = isCancelled(item);
                    return (
                      <tr key={item.id} className={getRowClasses(item)}>
                        {/* code + date */}
                        <td className='align-middle'>
                          {renderTransferWithDate(item.code, item.transferDate, cancelled)}
                        </td>

                        {/* accounts in one column */}
                        <td className='align-middle'>{renderFromToAccounts(item, cancelled)}</td>

                        {/* amounts in one column */}
                        <td className='align-middle'>
                          {renderAmountWithDetails(item.amount, item.tranferFees, item.transferVariance, cancelled)}
                        </td>

                        {/* confirm + posted */}
                        <td className='align-middle text-center'>{renderStatusWithPosting(item, cancelled)}</td>

                        {/* actions */}
                        <td className='align-middle'>
                          <div className='d-flex justify-content-center gap-2'>
                            <button
                              className={`btn btn-sm ${cancelled ? 'btn-outline-secondary' : 'btn-outline-theme'}`}
                              onClick={() => handleEdit(item)}
                              title='عرض التفاصيل'
                            >
                              <i className={`bi bi-info-circle fa-lg me-1 ${cancelled ? 'text-muted' : ''}`}></i>
                              <span className='d-none d-md-inline'>{cancelled ? 'عرض' : 'تفاصيل'}</span>
                            </button>

                            <Can permission={'FINEditCashTransfer'}>
                              <button
                                className='btn btn-sm btn-outline-info'
                                onClick={() => handleEdit(item)}
                                title='تعديل'
                                disabled={item.confirmFlag === true || item.postedFlag === true || cancelled}
                              >
                                <i className='fas fa-edit fa-lg me-1'></i>
                                <span className='d-none d-md-inline'>تعديل</span>
                              </button>
                            </Can>

                            <Can permission={'FINDeleteCashTransfer'}>
                              <button
                                className='btn btn-sm btn-outline-danger'
                                onClick={() => handleDelete(item.id)}
                                title='حذف'
                                disabled={item.confirmFlag === true || item.postedFlag === true || cancelled}
                              >
                                <i className='fas fa-trash fa-lg'></i>
                              </button>
                            </Can>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={5} className='text-center py-5 text-muted'>
                      <div className='py-4'>
                        <i className='fa fa-exchange-alt fa-3x mb-3 opacity-50'></i>
                        <h5 className='mb-2'>{activeFiltersCount > 0 ? 'لا توجد نتائج للبحث' : 'لا توجد تحويلات نقدية'}</h5>
                        <p className='text-muted small'>
                          {activeFiltersCount > 0 ? 'جرب تغيير شروط البحث أو إزالة بعض الفلاتر' : 'ابدأ بإنشاء تحويل نقدي جديد من زر (تحويل نقدي جديد)'}
                        </p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Loading>

        <CardFooter>
          <div className='text-theme'>
            <DynamicPagination tableKey={TABLE_KEY} maxVisiblePages={3} showItemsPerPage={true} />
          </div>
        </CardFooter>
      </Card>

      <DynamicFilter tableKey={TABLE_KEY} fields={fields} show={showFilters} onClose={() => setShowFilters(false)} />

      {/* Modals */}
      <NeatixModal
        show={showAddModal}
        setShow={setShowAddModal}
        size='lg'
        centered
        title='تحويل نقدي جديد'
        headerClassName='bg-theme bg-opacity-25'
      >
        <CashTransferForm
          onclose={() => {
            setShowAddModal(false);
            dispatch(getPagedData({ tableKey: TABLE_KEY }));
          }}
        />
      </NeatixModal>

      <NeatixModal
        show={showEditModal}
        setShow={setShowEditModal}
        size='lg'
        centered
        title='تعديل التحويل النقدي'
        headerClassName='bg-info bg-opacity-25'
      >
        <CashTransferForm
          initialData={selectedTransfer}
          isEditMode={true}
          onclose={() => {
            setShowEditModal(false);
            setSelectedTransfer(null);
            dispatch(getPagedData({ tableKey: TABLE_KEY }));
          }}
        />
      </NeatixModal>
    </>
  );
};

export default Index;
